import { useTranslation } from "react-i18next";

interface DebriefingAIDetailProps {
  data: {
    transcript?: string;
    feedback?: string;
    highlights?: string[];
    improvements?: string[];
  };
}

const DebriefingAIDetail = ({ data }: DebriefingAIDetailProps) => {
  const { t } = useTranslation();

  if (!data.transcript && !data.feedback) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Transcript */}
      {data.transcript && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {t("statsNew.interaction.debriefingAI.transcript")}
          </h4>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.transcript}</p>
          </div>
        </div>
      )}

      {/* Feedback */}
      {data.feedback && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {t("statsNew.interaction.debriefingAI.feedback")}
          </h4>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-sm text-blue-800 whitespace-pre-wrap">{data.feedback}</p>
          </div>
        </div>
      )}

      {/* Highlights */}
      {data.highlights && data.highlights.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {t("statsNew.interaction.debriefingAI.highlights")}
          </h4>
          <ul className="space-y-2">
            {data.highlights.map((highlight, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {highlight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Improvements */}
      {data.improvements && data.improvements.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            {t("statsNew.interaction.debriefingAI.improvements")}
          </h4>
          <ul className="space-y-2">
            {data.improvements.map((improvement, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                {improvement}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DebriefingAIDetail;
